import { Component, computed, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { Reservation, ReservationDocument, ReservationDocumentTypeEnum } from '@appTypes/api.types';
import { TranslatePipe } from '@ngx-translate/core';
import { dispatch, select } from '@ngxs/store';
import { ReservationInsuranceRowComponent } from './reservation-insurance-row/reservation-insurance-row.component';
import { reservationsDownloadDocument } from '@pages/reservations-page/state/reservations.action';
import { InsuranceAwareDocument } from '@pages/reservations-page/reservation-insurance.types';
import { ReservationsState } from '@pages/reservations-page/state/reservations.state';
import { ButtonLoadingDirective } from '@shared/directives/button-loading.directive';
import { DynamicModalData, OnDynamicModal } from '@shared/modules/dynamic-modal/dynamic-modal.types';
import { kebabCase, startCase } from 'lodash-es';

/**
 * The **Documents** popup of a reservation row: one line per document with its own Download.
 *
 * Whether a document may be downloaded is decided by the API — the popup only reflects what the
 * search response said when it was opened (`showDownloadDocuments`) and what the download endpoint
 * answers on click (a refused download surfaces through the state's error dialog, KM-5777).
 */
@Component({
  selector: 'app-reservation-docs',
  imports: [TranslatePipe, MatButton, ButtonLoadingDirective, ReservationInsuranceRowComponent],
  templateUrl: './reservation-docs.component.html'
})
export class ReservationDocsComponent implements OnDynamicModal<Reservation> {
  readonly #downloadDocument = dispatch(reservationsDownloadDocument);
  readonly #downloadLoadState = select(ReservationsState.downloadDocumentLoadState);

  readonly #reservation = signal<Reservation | null>(null);

  /** The reservation the popup was opened for — `null` until the modal hands it over. */
  public readonly reservation = this.#reservation.asReadonly();

  /** Documents listed in the popup. */
  public readonly documents = computed<InsuranceAwareDocument[]>(() => this.#reservation()?.documents ?? []);

  /**
   * `true` while a tourist's insurance line is drawn by the new cabinet states (the API sent the `insurance` block, engine `InsuranceService`).
   * With the engine `Core` no document carries it and the popup is exactly the classic one (A7 revert).
   */
  public readonly hasInsuranceLines = computed<boolean>(() => this.documents().some((d) => !!d.insurance));

  /** Whether Download is offered at all, as the search response said when the popup was opened. */
  public readonly canDownload = computed<boolean>(() => this.#reservation()?.showDownloadDocuments === true);

  /** `true` while any document download is in flight — every Download is disabled meanwhile. */
  public readonly isDownloading = computed<boolean>(() => this.#downloadLoadState().status === 'loading');

  /** URL of the document being downloaded right now, so only its button shows the spinner. */
  public readonly downloadingUrl = computed<string | null>(() => {
    const loadState = this.#downloadLoadState();
    return loadState.status === 'loading' ? (loadState.additionlData?.url ?? null) : null;
  });

  /** Receives the reservation from the dynamic modal. */
  public onDynamicModal(data: DynamicModalData<Reservation>): void {
    this.#reservation.set(data);
  }

  /**
   * The Type column's translation key: the document type in start case ("Avia Ticket", "Voucher" ...), except the airline's own
   * e-ticket (type 7, docs features/flight-ticket-attachment/qa.md R-TKF13), which reads "E-ticket" / "Электронный билет".
   */
  public typeLabel(document: ReservationDocument): string {
    return document.type === ReservationDocumentTypeEnum.AirlineTicket ? 'E-ticket' : startCase(document.type);
  }

  /**
   * The stable `data-testid` suffix of a row (C16): the type in kebab case plus the document id the download URL carries
   * (`airline-ticket-123` for the ticket file leg 123), so a test or a user script can pick one tourist's ticket.
   */
  public testIdSuffix(document: ReservationDocument): string {
    const documentId = documentIdOf(document.url);
    return documentId ? `${kebabCase(document.type)}-${documentId}` : kebabCase(document.type);
  }

  /** Downloads one document as `<reservation number> <Document Type>`. */
  public onDownloadDocument(document: ReservationDocument): void {
    if (!document.url) return;
    const fileName = `${this.#reservation()?.number ?? ''} ${this.typeLabel(document)}`.trim();
    this.#downloadDocument(document.url, fileName);
  }
}

/** The `documentId` query parameter of a GetDocument URL, or `null` when the URL has none. */
function documentIdOf(url: string | null | undefined): string | null {
  if (!url) return null;
  const query = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
  return new URLSearchParams(query).get('documentId');
}
